import { find } from 'lodash';
import { LocalDataSource } from 'ng2-smart-table';
import { Component, OnInit, Input, TemplateRef, ViewChild, Output, EventEmitter, OnDestroy } from '@angular/core';
import { TablaMaestra, Control, Static, BaseControl, ConfirmComponent, AccesoMaestra } from '@app/shared';
import { MasterAccessControlConfigService } from './shared/master-access-control.config.service';
import { MasterAccessControlFormsService } from './shared/master-access-control.form.service';
import { Subject } from 'rxjs';

@Component({
  selector: 'iw-master-access-control',
  templateUrl: './master-access-control.component.html',
  styleUrls: ['./master-access-control.component.scss'],
  providers: [MasterAccessControlFormsService]
})
export class MasterAccessControlComponent implements OnInit, OnDestroy {
  // Contiene la lista de maestras
  @Input() listMaster: Array<TablaMaestra> = [];
  // Contiene el control actual
  @Input() control: Control;
  // String que identifica la accion a realizar
  actionForm = 'Agregar';
  // Pregunta de la modal de confirmaciones
  question = '';
  // Define cuando se abre el formulario
  openForm = false;
  // Define si la accion es modificar
  update = false;
  // Contiene las maestras configuradas del control de acceso
  masterAccessControl: Array<AccesoMaestra> = [];
  // Contiene los controles del formulario
  controls: BaseControl<any>[] = [];
  // Contiene la configuracion de acceso seleccionada
  selectAccessConfig: AccesoMaestra;
  // Configuracion de la tabla
  settings = {};
  // Datos para llenar la tabla
  sourceData: LocalDataSource;
  // Destruye y gestiona el memory leak
  private destroy: Subject<void> = new Subject<void>();
  // Referencia a la plantilla para el control personalizado de ng-select para las maestras
  @ViewChild('ngSelect') ngSelectTpl: TemplateRef<any>;
  // Referencia al componente de confirmación
  @ViewChild('confirm') private confirm: ConfirmComponent;
  // Salida de las configuraciones de acceso a maestras agregadas al control
  @Output() configAccessControl: EventEmitter<Array<AccesoMaestra>> = new EventEmitter();

  constructor(private macfs: MasterAccessControlFormsService) { }

  ngOnInit() {
    const configTable = new MasterAccessControlConfigService();
    this.settings = configTable.settings;
    configTable.accion.subscribe((res: any) => {
      this.defineAction(res.action, res.data);
    });
    this.loadConfiguredMasterInformation();
  }

  ngOnDestroy() {
    this.destroy.next();
    this.destroy.complete();
  }

  /**
   * Controla las diferenctes acciones sobre la tabla y sus registros
   *
   * @memberof CreationOperationValueComponent
   */
  defineAction(action: 'add' | 'update' | 'delete' = 'add', data?: any) {
    switch (action) {
      case 'update':
        this.actionForm = 'Modificar';
        this.selectAccessConfig = data;
        this.controls = this.macfs.getForm(this.ngSelectTpl, this.listMaster, data);
        this.openForm = true;
        this.update = true;
        break;
      case 'delete':
        this.selectAccessConfig = data;
        this.question = '¿Realmente desea eliminar la configuración de acceso para la maestra?';
        this.confirm.open();
        break;
      default:
        this.actionForm = 'Agregar';
        this.selectAccessConfig = null;
        this.controls = this.macfs.getForm(this.ngSelectTpl, this.listMaster);
        this.openForm = true;
        this.update = false;
        break;
    }
  }

  /**
   * Cierra el formulario actual abierto
   *
   * @memberof CreationOperationValueComponent
   */
  closeForm() {
    this.openForm = false;
    this.controls = [];
    this.selectAccessConfig = null;
    this.update = false;
  }

  /**
   * Crea o edita las configuraciones de acceso
   *
   * @param {*} data
   * @memberof MasterAccessControlComponent
   */
  save(data: any) {
    const master = find(this.listMaster, {id: data.cboMasterTable});
    if (master) {
      const newAccessConfig: AccesoMaestra = {
        id_maestra: data.cboMasterTable,
        nombre_maestra: master.nombre,
        agregar: data.chAgregar,
        modificar: data.chModificar,
        eliminar: data.chEliminar
      };

      if (this.update) {
        if (this.selectAccessConfig['index'] >= 0) {
          this.masterAccessControl[this.selectAccessConfig['index']] = newAccessConfig;
        }
      } else {
        this.masterAccessControl.push(newAccessConfig);
      }
      this.loadInfoTable();
      this.closeForm();
    }
  }

  /**
   * Define la accion a realizar con la respuesta de la confirmacion
   *
   * @param {boolean} res
   * @memberof OperationsFormComponent
   */
  actionComfirm(res: boolean) {
    if (res) {
      this.deleteConfigAccess(this.selectAccessConfig['index']);
      this.selectAccessConfig = null;
    }
    this.question = '';
  }

  /**
   * Elimina una configuracion de acceso a una maestra
   *
   * @private
   * @param {number} index
   * @memberof MasterAccessControlComponent
   */
  private deleteConfigAccess(index: number) {
    this.masterAccessControl.splice(index, 1);
    this.loadInfoTable();
  }

  /**
   * Carga las maestras configuradas para el acceso a ellas desde los formularios
   * Esto aplica para el inicio cuando ya el control actual contiene estas configuraciones
   *
   * @private
   * @memberof MasterAccessControlComponent
   */
  private loadConfiguredMasterInformation() {
    if (
      this.control &&
      this.control.propiedades &&
      this.control.propiedades['control_acceso_maestra'] &&
      this.control.propiedades['control_acceso_maestra'].length
    ) {
      this.masterAccessControl = Static.copyObjet(this.control.propiedades['control_acceso_maestra']);
    }
    this.loadInfoTable();
  }

  /**
   * Carga la información para la tabla
   *
   * @private
   * @memberof MasterAccessControlComponent
   */
  private loadInfoTable() {
    if (this.masterAccessControl && this.masterAccessControl.length) {
      for (let index = 0; index < this.masterAccessControl.length; index++) {
        this.masterAccessControl[index]['index'] = index;
      }
    }
    this.sourceData = new LocalDataSource(this.masterAccessControl);
    this.emitConfigurations(this.masterAccessControl);
  }

  /**
   * Purga la informacion y la emite al componente padre
   *
   * @private
   * @param {Array<AccesoMaestra>} configurations
   * @memberof MasterAccessControlComponent
   */
  private emitConfigurations(configurations: Array<AccesoMaestra>) {
    const configurationspurge = Static.copyObjet(configurations);
    if (configurationspurge && configurationspurge.length) {
      for (let index = 0; index < configurationspurge.length; index++) {
        delete configurationspurge[index]['index'];
      }
    }
    this.configAccessControl.emit(configurationspurge);
  }

}



// WEBPACK FOOTER //
// ./src/app/administration/creation-dynamic-component/creation-form/master-access-control/master-access-control.component.ts